/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — toolbar: auto-arrange, snap toggle, team-boxes
   visibility dropdown, card style popover, print/export, keyboard
   hint. Ported from the old OrgChart.tsx toolbar section; NO "Hidden
   (N)" dropdown (People Rebuild §3 placement rules — removed, not
   ported). The Rounded/Square/Straight/Curved line-routing selector
   that used to live here is GONE (People Rebuild orthogonal-lines law,
   2026-08-18) — there is only one line style now (squared orthogonal,
   8px corner radius), so there is nothing left to pick.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { ChevronDown } from 'lucide-react';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import type { Team } from '../../../../shared/types';
import type { NodeStylePrefs } from './types';
import { CardStylePopover } from './CardStylePopover';
import { PrintExportButton } from './PrintExportButton';

export interface OrgChartToolbarProps {
  /** Editing affordances (Auto-arrange) are actually live RIGHT NOW —
   *  `canEdit permission && edit session active`. Renamed from the old
   *  `canEdit` to make that distinction explicit at the call site
   *  (People Rebuild edit-session grant, 2026-08-17). */
  editable: boolean;
  onAutoArrange: () => void;
  snapEnabled: boolean;
  setSnapEnabled: (v: boolean) => void;
  teams: Team[];
  visibleTeamIds: Record<string, boolean>;
  setTeamBoxVisible: (teamId: string, v: boolean) => void;
  setAllTeamsVisible: (v: boolean) => void;
  nodeStyle: NodeStylePrefs;
  setNodeStyle: (patch: Partial<NodeStylePrefs>) => void;
  /** Legend-overlay visibility (Josh grant 2026-08-19) — this button no
   *  longer opens an on-demand popover; it toggles the persistent
   *  on-canvas overlay OrgChartV2 shows in view mode. */
  legendVisible: boolean;
  setLegendVisible: (v: boolean) => void;
  printNodes: Node[];
  printEdges: Edge[];
  /** Saved-views picker + save/update/delete — owned by OrgChartV2 so
   *  this component stays unaware of views/sessions entirely. */
  viewsControls?: React.ReactNode;
  /** Right-aligned session controls — "Edit chart" button in View
   *  mode, or Save/Cancel/dirty-count/error while a session is open.
   *  Replaces the old static hint-pill markup, which is now built by
   *  the caller (copy differs per state). */
  sessionControls: React.ReactNode;
}

export function OrgChartToolbar({
  editable,
  onAutoArrange,
  snapEnabled,
  setSnapEnabled,
  teams,
  visibleTeamIds,
  setTeamBoxVisible,
  setAllTeamsVisible,
  nodeStyle,
  setNodeStyle,
  legendVisible,
  setLegendVisible,
  printNodes,
  printEdges,
  viewsControls,
  sessionControls,
}: OrgChartToolbarProps) {
  const anyTeamBoxVisible = Object.values(visibleTeamIds).some(Boolean);
  const [teamBoxMenuOpen, setTeamBoxMenuOpen] = useState(false);
  useEffect(() => {
    if (!teamBoxMenuOpen) return;
    const onDocClick = () => setTeamBoxMenuOpen(false);
    const t = setTimeout(() => document.addEventListener('mousedown', onDocClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [teamBoxMenuOpen]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: '0 var(--space-1)',
        marginBottom: rem(6),
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      {editable && (
        <button
          onClick={onAutoArrange}
          title="Reset positions and let the auto-layout arrange the chart top-down"
          aria-label="Auto-arrange the chart"
          style={{
            padding: rem(6) + ' var(--space-3)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500,
            background: 'var(--sf)',
            color: 'var(--tx)',
            border: '1px solid var(--bd)',
            borderRadius: rem(8),
            cursor: 'pointer',
          }}
        >
          Auto-arrange
        </button>
      )}

      <button
        onClick={() => setSnapEnabled(!snapEnabled)}
        title={snapEnabled ? 'Turn snap-to-align off' : 'Turn snap-to-align on'}
        aria-label={snapEnabled ? 'Turn snap-to-align off' : 'Turn snap-to-align on'}
        aria-pressed={snapEnabled}
        style={{
          padding: rem(6) + ' var(--space-3)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: snapEnabled ? 600 : 500,
          background: snapEnabled ? 'var(--ac)' : 'var(--sf)',
          color: snapEnabled ? 'var(--color-text-inverse)' : 'var(--tx)',
          border: '1px solid var(--bd)',
          borderRadius: rem(8),
          cursor: 'pointer',
          transition: 'background 120ms ease, color 120ms ease',
        }}
      >
        Snap {snapEnabled ? 'on' : 'off'}
      </button>

      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setTeamBoxMenuOpen((v) => !v)}
          title="Pick which team backdrops to show"
          aria-label="Team boxes visibility"
          aria-haspopup="menu"
          aria-expanded={teamBoxMenuOpen}
          style={{
            padding: rem(6) + ' var(--space-3)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: anyTeamBoxVisible ? 600 : 500,
            background: anyTeamBoxVisible ? 'var(--ac)' : 'var(--sf)',
            color: anyTeamBoxVisible ? 'var(--color-text-inverse)' : 'var(--tx)',
            border: '1px solid var(--bd)',
            borderRadius: rem(8),
            cursor: 'pointer',
            transition: 'background 120ms ease, color 120ms ease',
          }}
        >
          Team boxes
          {anyTeamBoxVisible && (
            <span style={{ marginLeft: rem(6), opacity: 0.85 }}>
              ({Object.values(visibleTeamIds).filter(Boolean).length})
            </span>
          )}
          <Icon
            icon={ChevronDown}
            em={1.0909}
            aria-hidden="true"
            style={{ marginLeft: rem(6), verticalAlign: -2 }}
          />
        </button>
        {teamBoxMenuOpen && (
          <div
            data-orgchart-menu
            role="menu"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 'var(--space-1)',
              zIndex: 1000,
              background: 'var(--sa)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              padding: rem(6),
              minWidth: rem(200),
              maxHeight: rem(320),
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                gap: rem(6),
                padding: 'var(--space-1) ' + rem(6),
                borderBottom: '1px solid var(--bd)',
                marginBottom: 'var(--space-1)',
              }}
            >
              <button
                onClick={() => setAllTeamsVisible(true)}
                title="Show every team's backdrop"
                style={{
                  flex: 1,
                  padding: 'var(--space-1) var(--space-2)',
                  fontSize: 'var(--font-size-xs)',
                  background: 'var(--sf)',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                Show all
              </button>
              <button
                onClick={() => setAllTeamsVisible(false)}
                title="Hide every team's backdrop"
                style={{
                  flex: 1,
                  padding: 'var(--space-1) var(--space-2)',
                  fontSize: 'var(--font-size-xs)',
                  background: 'var(--sf)',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                Hide all
              </button>
            </div>
            {teams.length === 0 && (
              <div
                style={{
                  padding: 'var(--space-2) ' + rem(6),
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--tm)',
                }}
              >
                No teams yet
              </div>
            )}
            {teams.map((team) => {
              const checked = !!visibleTeamIds[team.id];
              return (
                <label
                  key={team.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: rem(6) + ' var(--space-2)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setTeamBoxVisible(team.id, e.target.checked)}
                  />
                  <span
                    style={{
                      width: rem(10),
                      height: rem(10),
                      borderRadius: rem(3),
                      background: team.color || 'var(--ac)',
                      flexShrink: 0,
                    }}
                  />
                  <span>{team.name}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <CardStylePopover value={nodeStyle} onChange={setNodeStyle} />

      <button
        onClick={() => setLegendVisible(!legendVisible)}
        title={
          legendVisible
            ? 'Hide the on-screen badge legend (view mode)'
            : 'Show the on-screen badge legend (view mode)'
        }
        aria-label={legendVisible ? 'Hide chart legend' : 'Show chart legend'}
        aria-pressed={legendVisible}
        style={{
          padding: rem(6) + ' var(--space-3)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: legendVisible ? 600 : 500,
          background: legendVisible ? 'var(--ac)' : 'var(--sf)',
          color: legendVisible ? 'var(--color-text-inverse)' : 'var(--tx)',
          border: '1px solid var(--bd)',
          borderRadius: rem(8),
          cursor: 'pointer',
          transition: 'background 120ms ease, color 120ms ease',
        }}
      >
        Legend {legendVisible ? 'on' : 'off'}
      </button>

      <PrintExportButton nodes={printNodes} edges={printEdges} teams={teams} />

      {viewsControls}

      <div style={{ flex: 1 }} />
      {sessionControls}
    </div>
  );
}
